import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  V2Card,
  V2CardHeader,
  V2Badge,
  V2CheckRow,
  V2DataGrid,
  V2EmptyState,
  V2FormField,
  V2FormGrid,
  Icon,
  LoadingState,
  StatePanel,
} from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { JalaliDateInput } from '../../lib/date/JalaliDateInput.js';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';
import { formatRialFa, type AmountDisplayUnit } from '../../lib/finance-display.js';
import { useAmountDisplay } from '../../lib/tenantSettings.js';
import type { TrialBalanceResponse } from '../../lib/types/finance.js';
import { useFinanceBook } from './useFinanceBook.js';

type TrialBalanceRow = TrialBalanceResponse['rows'][number];

/** Debit/credit nature is spoken with "بد"/"بس" text, never color alone —
 * matches the design's own explicit rule ("ماهیت مانده با «بد» و «بس»
 * گفته می‌شود، نه با رنگ"). Exactly one side is non-zero per row on a real
 * trial balance; both zero renders a plain dash. */
function balanceLabel(
  row: { debitBalanceRial: string; creditBalanceRial: string },
  unit: AmountDisplayUnit,
): string {
  const debit = BigInt(row.debitBalanceRial);
  const credit = BigInt(row.creditBalanceRial);
  if (debit > 0n) return `${formatRialFa(row.debitBalanceRial, unit)} بد`;
  if (credit > 0n) return `${formatRialFa(row.creditBalanceRial, unit)} بس`;
  return '—';
}

/**
 * UI_REFERENCE_SOURCE:
 * section: Sakhtar Finance.dc.html §11 "REPORT DETAIL · TRIAL BALANCE ·
 *   900px"
 * archetype: filtered report table + totals row + always-shown basis
 *   footnote
 * status: PARITY — real endpoint (`GET .../finance/reports/trial-
 *   balance`, ERP-168, `apps/api/src/finance/reports.controller.ts`),
 *   posted-GL-only, official. The design's "سطح: معین" (account level)
 *   filter and its "دورهٔ مالی" (fiscal period) date alternative are NOT
 *   built: the API accepts only `asOfDate`/`fiscalPeriodCode`, no level
 *   parameter (no client-side GL rollup to fake one — this page does no
 *   accounting, same rule as TreasuryPage.tsx) — and `GET .../accounting/
 *   fiscal-periods` never serializes a period's `code`
 *   (`fiscal-period.controller.ts` only returns fiscalPeriodId/name/...),
 *   which the report's `fiscalPeriodCode` param needs — a real, separate
 *   backend gap, not faked with `name`. `asOfDate` alone is real and
 *   sufficient for V1. "فقط حساب‌های با گردش" IS implemented, honestly,
 *   as a client-side filter over the real, already-fetched rows (hides
 *   rows where both movement columns are zero) — filtering, not
 *   computing. No export button, matching this app's one centralized
 *   `/data` import/export route rather than a per-report button. A print
 *   button IS added (2026-09-28) — `routes/print/TrialBalancePrintPage.tsx`,
 *   the same A4 print chrome the CRM quote/order/customer-report views
 *   already use — carrying the picked `asOfDate` through the query string.
 * state reference: ready (no date picked yet) · loading · empty (no
 *   posted activity) · permission_denied · network_error · validation
 *   (invalid asOfDate)
 */
export function TrialBalancePage() {
  const { unit, label } = useAmountDisplay();
  const { slug } = useParams<{ slug: string }>();
  const base = slug ? tenantBasePath(slug) : '';
  const [asOfDate, setAsOfDate] = useState('');
  const [onlyWithActivity, setOnlyWithActivity] = useState(false);
  const book = useFinanceBook();
  const bookId = book.bookId;

  // The design's own "دورهٔ مالی" filter alternative isn't wired: GET
  // .../accounting/fiscal-periods never serializes `code`
  // (fiscal-period.controller.ts only returns fiscalPeriodId/name/...),
  // and the report's own `fiscalPeriodCode` query param needs exactly
  // that — a real, separate backend gap, not something to fake with
  // `name`. `asOfDate` alone is fully real and sufficient for V1.
  const reportUrl =
    slug && asOfDate && book.ready
      ? `${base}/api/v1/finance/reports/trial-balance?asOfDate=${asOfDate}${bookId ? `&bookId=${bookId}` : ''}`
      : null;
  const report = useApiGet<TrialBalanceResponse>(reportUrl, [slug, asOfDate, bookId]);

  const rows = useMemo(() => {
    if (report.status !== 'ready') return [];
    return report.data.rows.filter(
      (r) =>
        !onlyWithActivity || BigInt(r.debitMovementRial) > 0n || BigInt(r.creditMovementRial) > 0n,
    );
  }, [report, onlyWithActivity]);

  if (!slug) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <V2Card>
        <V2CardHeader
          title="تراز آزمایشی"
          subtitle="مانده حساب‌ها در تاریخ معین — فقط اسناد قطعی"
        />
        <div style={{ padding: '0 20px 18px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <V2FormGrid>
            <V2FormField label="تا تاریخ" width="medium">
              <JalaliDateInput value={asOfDate} onChange={setAsOfDate} />
            </V2FormField>
          </V2FormGrid>
          <V2CheckRow
            label="فقط حساب‌های با گردش"
            checked={onlyWithActivity}
            onChange={setOnlyWithActivity}
          />
        </div>
      </V2Card>

      {!asOfDate ? (
        <V2EmptyState
          icon="finance"
          title="تاریخ را انتخاب کنید"
          description="تراز آزمایشی پس از انتخاب تاریخ نمایش داده می‌شود."
        />
      ) : null}

      {report.status === 'loading' ? <LoadingState /> : null}
      {report.status === 'error' ? (
        <StatePanel
          variant={
            report.error.status === 403
              ? 'permission_denied'
              : report.error.status === 422
                ? 'error'
                : 'network_error'
          }
          title={
            report.error.status === 403
              ? 'دسترسی ندارید'
              : report.error.status === 422
                ? 'تاریخ نامعتبر است'
                : 'تراز آزمایشی قابل نمایش نیست'
          }
        />
      ) : null}

      {report.status === 'ready' ? (
        <V2DataGrid
          icon="finance"
          title="مانده حساب‌ها"
          subtitle={`${rows.length.toLocaleString('fa-IR')} حساب`}
          actions={
            <>
              <V2Badge tone={report.data.balanced ? 'success' : 'danger'}>
                {report.data.balanced ? 'تراز' : 'نامتوازن'}
              </V2Badge>
              <Link
                to={`${base}/finance/reports/trial-balance/print?asOfDate=${asOfDate}`}
                className="sk-btn sk-btn--secondary sk-btn--sm"
              >
                <Icon name="invoice" size="sm" />
                چاپ
              </Link>
            </>
          }
          densitySwitch={false}
          pageSize={0}
          columns={[
            {
              key: 'code',
              header: 'کد',
              width: 100,
              render: (row: TrialBalanceRow) => (
                <span className="ltr sk-num" style={{ fontWeight: 600 }}>
                  {row.accountRef}
                </span>
              ),
              footer: 'مجموع',
            },
            {
              key: 'name',
              header: 'عنوان حساب',
              width: 200,
              wrap: true,
              render: (row: TrialBalanceRow) => row.accountName,
            },
            {
              key: 'debit',
              header: 'گردش بدهکار',
              align: 'end',
              emphasis: 'group',
              width: 140,
              render: (row: TrialBalanceRow) => (
                <span className="sk-num">
                  {BigInt(row.debitMovementRial) > 0n
                    ? formatRialFa(row.debitMovementRial, unit)
                    : '—'}
                </span>
              ),
              footer: (
                <span className="sk-num">
                  {formatRialFa(report.data.totals.debitMovementRial, unit)}
                </span>
              ),
            },
            {
              key: 'credit',
              header: 'گردش بستانکار',
              align: 'end',
              emphasis: 'group',
              width: 140,
              render: (row: TrialBalanceRow) => (
                <span className="sk-num">
                  {BigInt(row.creditMovementRial) > 0n
                    ? formatRialFa(row.creditMovementRial, unit)
                    : '—'}
                </span>
              ),
              footer: (
                <span className="sk-num">
                  {formatRialFa(report.data.totals.creditMovementRial, unit)}
                </span>
              ),
            },
            {
              key: 'balance',
              header: 'مانده',
              align: 'end',
              emphasis: 'key',
              width: 150,
              render: (row: TrialBalanceRow) => (
                <span className="sk-num">{balanceLabel(row, unit)}</span>
              ),
              footer: <span className="sk-num">{balanceLabel(report.data.totals, unit)}</span>,
            },
          ]}
          rows={rows}
          rowKey={(row) => row.accountRef}
          legendNote={`ارقام به ${label} · تا تاریخ ${displayJalaliDate(report.data.asOfDate)} · فقط اسناد قطعی`}
          emptyState={
            <V2EmptyState
              icon="finance"
              title="سندی قطعی برای این بازه ثبت نشده"
              description="با ثبت و قطعی‌شدن اسناد حسابداری، تراز اینجا نمایش داده می‌شود."
            />
          }
        />
      ) : null}
    </div>
  );
}
